export const BASE = '/api/v1'

export class ApiError extends Error {
  status: number
  detail: string
  type: string | undefined

  constructor(status: number, detail: string, type?: string) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
    this.type = type
  }

  get isUnauthorized() {
    return this.status === 401
  }

  get isForbidden() {
    return this.status === 403
  }

  get isNotFound() {
    return this.status === 404
  }

  get isRateLimited() {
    return this.status === 429
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new ApiError(res.status, body.detail || res.statusText, body.type)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

function buildUrl(path: string, params?: Record<string, unknown>): string {
  const url = new URL(`${BASE}${path}`, window.location.origin)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.toString()
}

interface RequestOptions {
  signal?: AbortSignal
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  params?: Record<string, unknown>,
  options?: RequestOptions
): Promise<T> {
  const res = await fetch(buildUrl(path, params), {
    method,
    credentials: 'include',
    signal: options?.signal,
    ...(body !== undefined
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      : {}),
  })
  return handleResponse<T>(res)
}

export const get = <T>(path: string, params?: Record<string, unknown>, options?: RequestOptions) =>
  request<T>('GET', path, undefined, params, options)

export const post = <T>(path: string, body?: unknown, options?: RequestOptions) =>
  request<T>('POST', path, body, undefined, options)

export const put = <T>(path: string, body?: unknown, options?: RequestOptions) =>
  request<T>('PUT', path, body, undefined, options)

export const patch = <T>(path: string, body?: unknown, options?: RequestOptions) =>
  request<T>('PATCH', path, body, undefined, options)

export const del = <T>(path: string, options?: RequestOptions) =>
  request<T>('DELETE', path, undefined, undefined, options)
